import showNotificationOnMarkAsJunk from './helpers/showNotificationOnMarkAsJunk';
import moveItemsService from '../../services/moveItemsService';
import { dontReportPhishing, reportPhishing } from '../../strings.locstring.json';
import datapoints from '../datapoints';
import { Checkbox } from '@fluentui/react/lib/Checkbox';
import { ActionSource, createLegacyActionWithDatapoint } from 'owa-analytics-actions';
import { getApolloClient } from 'owa-apollo';
import { confirm, DialogResponse } from 'owa-confirm-dialog';
import { MailboxInfoInput } from 'owa-graph-schema';
import loc from 'owa-localize';
import { ignore_dontShowAgainCheckbox } from 'owa-locstrings/lib/strings/ignore_dontshowagaincheckbox.locstring.json';
import { userMailInteractionAction } from 'owa-mail-actions';
import { TableView } from 'owa-mail-list-store';
import { InstrumentationContext } from 'owa-search/lib/types/InstrumentationContext';
import { getMailboxInfo } from 'owa-mail-mailboxinfo';
import * as undoActions from 'owa-mail-undo';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import folderNameToId from 'owa-session-store/lib/utils/folderNameToId';
import isConsumer from 'owa-session-store/lib/utils/isConsumer';
import { updateUserConfigurationAndService } from 'owa-session-store/lib/utils/updateUserConfigurationAndService';
import * as React from 'react';
import {
    reportAsJunk,
    reportAsNotJunk,
    reportJunkMessageBody,
    reportJunkMessageEnterpriseMessageBody,
} from './markItemsJunkNotJunkBasedOnItemIds.locstring.json';

import {
    MarkItemAsJunkDocument,
    MarkItemAsJunkMutation,
} from '../../graphql/__generated__/markItemAsJunkMutation.interface';
import {
    UndoMarkItemAsJunkDocument,
    UndoMarkItemAsJunkMutation,
} from '../../graphql/__generated__/undoMarkItemAsJunkMutation.interface';

type MarkItemAsJunkResult = MarkItemAsJunkMutation['markItemAsJunk'];
type UndoMarkItemAsJunkResult = UndoMarkItemAsJunkMutation['undoMarkItemAsJunk'];

/**
 * mark item as junk action
 * @param itemIds of the items to be marked as junk/not junk
 * @param isJunkValueToSet used to determine whether the item should be marked as junk
 * @param actionSource the action source where this action is triggered
 */
export default createLegacyActionWithDatapoint(
    'markItemsJunkNotJunkBasedOnItemIds',
    datapoints.markItemsJunkNotJunk
)(async function markItemsJunkNotJunkBasedOnItemIds(
    itemIds: string[],
    isJunkValueToSet: boolean,
    actionSource: ActionSource,
    tableView: TableView,
    instrumentationContexts: InstrumentationContext[],
    markItemsJunkNotJunkStoreUpdate: () => void
) {
    const allowUndo = undoActions.doesTableSupportUndo(
        tableView?.tableQuery?.folderId,
        getMailboxInfo(tableView)
    );
    await undoActions.clearLastUndoableAction();

    const reportJunkEmailEnabledByAdmin = getUserConfiguration().SegmentationSettings
        .ReportJunkEmailEnabled;
    const reportJunkSelected = getUserConfiguration().UserOptions.ReportJunkSelected;
    const skipCheckForReportJunkDialog = getUserConfiguration().UserOptions
        .CheckForReportJunkDialog;
    let isChecked = false;
    const isCheckedDialog = () => {
        isChecked = !isChecked;
    };

    let promiseToReturn = Promise.resolve();
    let promiseToUndo;
    let reportedToSafetyLibrary = false;

    const mailboxInfo = getMailboxInfo(tableView);

    try {
        if (
            // Add check for Message Safety Reporting after Bug 35457([OwaClientMessageSafetyReporting] disabled in React for EXO BlackForest, Gallatin and ITAR) is resolved
            isConsumer() ||
            (skipCheckForReportJunkDialog &&
                reportJunkSelected &&
                reportJunkEmailEnabledByAdmin &&
                isJunkValueToSet)
        ) {
            promiseToUndo = exportedHelperFunctions.invokeMarkItemAsJunkMutation(
                itemIds,
                isJunkValueToSet,
                true,
                mailboxInfo
            );
            reportedToSafetyLibrary = true;
        } else if (
            !reportJunkEmailEnabledByAdmin ||
            (skipCheckForReportJunkDialog && isJunkValueToSet)
        ) {
            promiseToUndo = exportedHelperFunctions.invokeMarkItemAsJunkMutation(
                itemIds,
                isJunkValueToSet,
                false,
                mailboxInfo
            );
        } else {
            //show dialog box
            promiseToReturn = confirm(
                isJunkValueToSet ? loc(reportAsJunk) : loc(reportAsNotJunk),
                isConsumer
                    ? loc(reportJunkMessageBody)
                    : loc(reportJunkMessageEnterpriseMessageBody),
                false,
                {
                    okText: loc(reportPhishing),
                    cancelText: loc(dontReportPhishing),
                    bodyElement: isJunkValueToSet ? (
                        <Checkbox
                            label={loc(ignore_dontShowAgainCheckbox)}
                            onChange={isCheckedDialog}
                        />
                    ) : null,
                }
            ).then((response: DialogResponse) => {
                if (isJunkValueToSet) {
                    const isReportJunkSelected = response === DialogResponse.ok;
                    updateUserConfigurationAndService(
                        config => {
                            config.UserOptions.ReportJunkSelected = isReportJunkSelected;
                            config.UserOptions.CheckForReportJunkDialog = isChecked;
                        },
                        [
                            {
                                key: 'CheckForReportJunkDialog',
                                valuetype: 'Boolean',
                                value: [String(isChecked)],
                            },
                            {
                                key: 'ReportJunkSelected',
                                valuetype: 'Boolean',
                                value: [String(isReportJunkSelected)],
                            },
                        ]
                    );
                }
                if (response == DialogResponse.ok) {
                    promiseToUndo = exportedHelperFunctions.invokeMarkItemAsJunkMutation(
                        itemIds,
                        isJunkValueToSet,
                        true,
                        mailboxInfo
                    );
                    reportedToSafetyLibrary = true;
                } else {
                    promiseToUndo = exportedHelperFunctions.invokeMarkItemAsJunkMutation(
                        itemIds,
                        isJunkValueToSet,
                        false,
                        mailboxInfo
                    );
                }
            });
        }
    } catch (error) {
        // VSO 19006: Handle triage action service error
    }

    if (allowUndo) {
        undoActions.addActionToUndoStack(
            async () => {
                // Wait for mark junk promise and make the opposite call after
                const response = await promiseToUndo;
                if (response.success) {
                    await processUndoReportAsJunk(
                        reportedToSafetyLibrary,
                        isJunkValueToSet,
                        response.movedItemIds,
                        tableView,
                        response.undoRequestId
                    );
                }
                return Promise.resolve();
            },
            tableView.serverFolderId,
            promiseToUndo
        );
    }

    if (actionSource) {
        // If there is a confirmation dialog, wait until after input to display status bar
        promiseToReturn.then(() =>
            showNotificationOnMarkAsJunk(itemIds.length, allowUndo, isJunkValueToSet)
        );
    }

    markItemsJunkNotJunkStoreUpdate();
    if (isJunkValueToSet) {
        userMailInteractionAction('MarkAsJunk', instrumentationContexts, { itemIds });
    }

    return promiseToReturn;
});

async function processUndoReportAsJunk(
    reportedToSafetyLibrary: boolean,
    isJunkValueToSet: boolean,
    movedItemIds: string[],
    tableView: TableView,
    undoRequestId: number | null | undefined
) {
    if (movedItemIds.length > 0) {
        const mailboxInfo = getMailboxInfo(tableView);

        const undoActionPromise = exportedHelperFunctions.invokeUndoMarkItemAsJunkMutation(
            movedItemIds,
            !isJunkValueToSet,
            reportedToSafetyLibrary,
            mailboxInfo,
            undoRequestId
        );

        // If we are undoing a mark as junk action and not in inbox, the mark as not
        // junk will send the item to the inbox. So after the undone service call, make another move command
        // to send the item back to the original folder
        const inboxFolderId = folderNameToId('inbox');
        if (isJunkValueToSet && inboxFolderId !== tableView.tableQuery.folderId) {
            const responseForUndoAction = await undoActionPromise;
            const movedItemIdsForUndoAction = responseForUndoAction.movedItemIds;
            if (movedItemIdsForUndoAction.length > 0) {
                return moveItemsService(
                    movedItemIdsForUndoAction,
                    tableView.tableQuery.folderId,
                    inboxFolderId
                );
            }
        }
        return undoActionPromise;
    }
    return Promise.resolve();
}

const invokeMarkItemAsJunkMutationFn = async function invokeMarkItemAsJunkMutation(
    ids: string[],
    isJunk: boolean,
    reportToSafety: boolean,
    mailboxInfo: MailboxInfoInput
): Promise<MarkItemAsJunkResult> {
    const client = await getApolloClient();
    const result = await client.mutate({
        variables: {
            ids: ids,
            isJunk: isJunk,
            reportToSafety: reportToSafety,
            mailboxInfo: mailboxInfo,
        },
        mutation: MarkItemAsJunkDocument,
    });

    return result?.data?.markItemAsJunk;
};

const invokeUndoMarkItemAsJunkMutationFn = async function invokeUndoMarkItemAsJunkMutation(
    ids: string[],
    isJunk: boolean,
    reportToSafety: boolean,
    mailboxInfo: MailboxInfoInput,
    undoRequestId: number | null | undefined
): Promise<UndoMarkItemAsJunkResult> {
    const client = await getApolloClient();
    const result = await client.mutate({
        variables: {
            ids: ids,
            isJunk: isJunk,
            reportToSafety: reportToSafety,
            mailboxInfo: mailboxInfo,
            undoRequestId: undoRequestId,
        },
        mutation: UndoMarkItemAsJunkDocument,
    });

    return result?.data?.undoMarkItemAsJunk;
};

/**
 * This helper method is exported so it can be mocked for unit testing
 */
export const exportedHelperFunctions = {
    invokeMarkItemAsJunkMutation: invokeMarkItemAsJunkMutationFn,
    invokeUndoMarkItemAsJunkMutation: invokeUndoMarkItemAsJunkMutationFn,
};
